/**
 * Pricing constants for country-based pricing
 * 
 * Supported currencies:
 * - USD ($) - Default for all countries except IN, GB, and European countries
 * - EUR (€) - European countries
 * - GBP (£) - Great Britain
 * - INR (₹) - India
 */

export type SupportedCurrency = 'usd' | 'eur' | 'gbp' | 'inr';

export interface PlanPricing {
  monthly: number;
  yearly: number;
}

export interface CurrencyPricing {
  starter: PlanPricing;
  professional: PlanPricing;
}

/**
 * Pricing for each supported currency
 */
export const CURRENCY_PRICING: Record<SupportedCurrency, CurrencyPricing> = {
  usd: {
    starter: {
      monthly: 9.99,
      yearly: 99.00,
    },
    professional: {
      monthly: 19.99,
      yearly: 199.00,
    },
  },
  eur: {
    starter: {
      monthly: 8.50,
      yearly: 85.00,
    },
    professional: {
      monthly: 17.10,
      yearly: 171.00,
    },
  },
  gbp: {
    starter: {
      monthly: 7.50,
      yearly: 75.00,
    },
    professional: {
      monthly: 14.90,
      yearly: 149.00,
    },
  },
  inr: {
    starter: {
      monthly: 499.00,
      yearly: 4999.00,
    },
    professional: {
      monthly: 1499.00,
      yearly: 14999.00,
    },
  },
};

/**
 * Currency symbols for display
 */
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
  inr: '₹',
};

/**
 * European countries that use EUR
 */
const EUROPEAN_COUNTRIES = [
  'AT', // Austria
  'BE', // Belgium
  'CY', // Cyprus
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PT', // Portugal
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
];

/**
 * Get the currency for a given country code
 * - India (IN) -> INR
 * - Great Britain (GB) -> GBP
 * - European countries -> EUR
 * - All others -> USD
 */
export function getCurrencyForCountry(countryCode: string): SupportedCurrency {
  const code = countryCode.toUpperCase();
  
  if (code === 'IN') {
    return 'inr';
  }
  
  if (code === 'GB') {
    return 'gbp';
  }
  
  if (EUROPEAN_COUNTRIES.includes(code)) {
    return 'eur';
  }
  
  return 'usd';
}

/**
 * Get currency symbol for display
 */
export function getCurrencySymbol(currency: string): string {
  const normalizedCurrency = currency.toLowerCase() as SupportedCurrency;
  return CURRENCY_SYMBOLS[normalizedCurrency] || '$';
}

/**
 * Get pricing for a specific currency and plan
 */
export function getPricing(currency: SupportedCurrency, plan: 'starter' | 'professional'): PlanPricing {
  return CURRENCY_PRICING[currency][plan];
}

/**
 * Format price with currency symbol
 */
export function formatPrice(amount: number, currency: SupportedCurrency): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  
  // INR typically doesn't show decimals for whole numbers
  if (currency === 'inr' && amount % 1 === 0) {
    return `${symbol}${amount.toLocaleString('en-IN')}`;
  }
  
  return `${symbol}${amount.toFixed(2)}`;
}
